import { useState } from 'react';
import { toast } from 'sonner';
import {
  enablePushSubscription,
  disablePushSubscription,
  getPwaPushApiBase,
  getOsCategory,
  getBlockedHintCopy,
} from '@/lib/pwa-push';
import { useBrowserNotificationPermission } from '@/hooks/use-browser-notification-permission';
import { NotificationBlockedBanner } from '@/components/NotificationBlockedBanner';

const NOTIFICATIONS_ENABLED_KEY = 'notifications-enabled';

export function NotificationsPage() {
  const [enabled, setEnabled] = useState(() => {
    const stored = localStorage.getItem(NOTIFICATIONS_ENABLED_KEY);
    return stored === 'true';
  });
  const [isSubscribing, setIsSubscribing] = useState(false);
  const browserPermission = useBrowserNotificationPermission();

  const handleToggle = async () => {
    const apiBase = getPwaPushApiBase();
    const next = !enabled;

    if (next) {
      if (!apiBase) {
        toast.error('Notifications unavailable', {
          description: 'App configuration error. Please try again later.',
        });
        return;
      }
      setEnabled(true);
      localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, 'true');
      setIsSubscribing(true);
      try {
        const err = await enablePushSubscription(apiBase);
        if (err) {
          toast.error('Notifications unavailable', { description: err });
        } else {
          toast.success('Notifications enabled');
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to enable push notifications';
        toast.error('Notifications unavailable', { description: msg });
      } finally {
        setIsSubscribing(false);
      }
    } else {
      setEnabled(false);
      localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, 'false');
      if (apiBase) {
        disablePushSubscription(apiBase).then(err => {
          if (err) toast.error('Could not clear subscription', { description: err });
        });
      }
    }
  };

  const showBlockedBanner = enabled && browserPermission === 'denied';

  return (
    <div
      className="app-shell"
      style={{ flexDirection: 'column', justifyContent: 'flex-start', paddingTop: '3rem' }}
    >
      <div className="install-card">
        <img src="/icon-192.png" alt="" className="install-logo" width={96} height={96} />
        <h1 className="install-title">Notifications</h1>
        <p className="install-description">Manage your push notification settings.</p>
        <label
          className="notifications-toggle"
          style={{
            marginTop: '1rem',
            opacity: isSubscribing ? 0.7 : 1,
            pointerEvents: isSubscribing ? 'none' : 'auto',
          }}
        >
          <span className="notifications-toggle__label">Allow Notifications</span>
          <input
            type="checkbox"
            checked={enabled}
            onChange={handleToggle}
            disabled={isSubscribing}
            className="notifications-toggle__input"
            aria-label="Allow notifications"
          />
          <span className="notifications-toggle__track" aria-hidden />
        </label>
        {showBlockedBanner && (
          <NotificationBlockedBanner message={getBlockedHintCopy(getOsCategory())} />
        )}
      </div>
    </div>
  );
}

export default NotificationsPage;
